// scanner-fp: `useCallback` deps intentionally omit `api` (module singleton) and setters (stable). See /app/SECURITY_SCANNER_IGNORE.md §4.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, XCircle, Clock, Send, Paperclip, Download,
  AlertTriangle, Play, MessageSquare, CalendarClock, User as UserIcon, FileText,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import AttachmentComposer from "@/components/AttachmentComposer";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const STATUS_META = {
  open: { label: "Open", cls: "bg-slate-500/15 text-slate-600 border-slate-500/30" },
  assigned: { label: "Awaiting acceptance", cls: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
  accepted: { label: "Accepted", cls: "bg-blue-500/15 text-blue-600 border-blue-500/30" },
  declined: { label: "Declined", cls: "bg-rose-500/15 text-rose-600 border-rose-500/30" },
  in_progress: { label: "In progress", cls: "bg-blue-500/15 text-blue-600 border-blue-500/30" },
  closure_requested: { label: "Closure pending review", cls: "bg-purple-500/15 text-purple-600 border-purple-500/30" },
  extension_requested: { label: "Extension pending review", cls: "bg-purple-500/15 text-purple-600 border-purple-500/30" },
  completed: { label: "Completed", cls: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
  done: { label: "Done", cls: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
  reopened: { label: "Reopened", cls: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
  cancelled: { label: "Cancelled", cls: "bg-slate-500/15 text-slate-500 border-slate-500/30" },
};

function fmtDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch (_e) { return iso; }
}

function timeLeft(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  const d = Math.floor(Math.abs(ms) / 86_400_000);
  const h = Math.floor((Math.abs(ms) % 86_400_000) / 3_600_000);
  if (ms < 0) return { overdue: true, label: `${d}d ${h}h overdue` };
  return { overdue: false, label: d > 0 ? `${d}d ${h}h left` : `${h}h left` };
}

export default function TaskDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();

  const [task, setTask] = useState(null);
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeNote, setCloseNote] = useState("");
  const [closeFiles, setCloseFiles] = useState([]);
  const [extOpen, setExtOpen] = useState(false);
  const [extDate, setExtDate] = useState("");
  const [extReason, setExtReason] = useState("");
  const [decOpen, setDecOpen] = useState(null); // 'closure' | 'extension'
  const [decNote, setDecNote] = useState("");
  const [decNewDue, setDecNewDue] = useState("");
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, th] = await Promise.all([
        api.get(`/crm/tasks/${id}`),
        api.get(`/crm/tasks/${id}/thread`),
      ]);
      setTask(t.data);
      setThread(th.data);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 100);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load task");
      nav("/app/crm/tasks");
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  // ----- Role gating -----
  const isAssignee = task && user?.id === task.assigned_to;
  const isOwner = task && user?.id === task.owner_id;
  const isAdmin = user?.role === "admin";
  const canAdminAct = isOwner || isAdmin;
  // Both parties can chat
  const canChat = task && (isAssignee || canAdminAct);

  // ----- Action handlers -----
  const call = async (path, body, successMsg) => {
    try {
      await api.post(path, body || {});
      successMsg && toast.success(successMsg);
      await load();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
      return false;
    }
  };

  const send = async () => {
    if (!msg.trim() && attachments.length === 0) return;
    const ok = await call(`/crm/tasks/${id}/progress`, {
      note: msg || "(sent attachments)",
      file_ids: attachments.map(f => f.id),
    });
    if (ok) {
      setMsg("");
      setAttachments([]);
    }
  };

  if (loading || !task) {
    return <div className="text-sm text-muted-foreground py-8">Loading task…</div>;
  }

  const sMeta = STATUS_META[task.status] || STATUS_META.open;
  const tl = timeLeft(task.due_at);
  const adminDocs = task.files?.filter(f => (task.file_ids || []).includes(f.id)) || [];
  const empDocs = task.files?.filter(f => (task.employee_file_ids || []).includes(f.id)) || [];

  return (
    <div className="space-y-4 max-w-4xl mx-auto pb-8">
      {/* Header */}
      <Button variant="ghost" size="sm" onClick={() => nav("/app/crm/tasks")} data-testid="task-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> All tasks
      </Button>
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${sMeta.cls}`} data-testid="task-status-chip">
                {sMeta.label}
              </span>
              {task.priority === "high" && (
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-rose-500/15 text-rose-600 border border-rose-500/30">
                  High priority
                </span>
              )}
            </div>
            <h1 className="font-display text-2xl font-light">{task.title}</h1>
            {task.description && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{task.description}</p>}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Assignee</div>
            <div className="mt-0.5 font-medium flex items-center gap-1"><UserIcon className="w-3 h-3" /> {task.assigned_to ? task.assigned_to.slice(0,8) : "Unassigned"}</div>
          </div>
          <div>
            <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Due</div>
            <div className="mt-0.5 font-medium">{fmtDT(task.due_at)}</div>
            {tl && (
              <div className={`mt-0.5 text-[11px] inline-flex items-center gap-1 ${tl.overdue ? "text-rose-600" : "text-muted-foreground"}`}>
                {tl.overdue ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                {tl.label}
              </div>
            )}
          </div>
          {task.original_due_at && task.original_due_at !== task.due_at && (
            <div>
              <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Original due</div>
              <div className="mt-0.5 line-through text-muted-foreground">{fmtDT(task.original_due_at)}</div>
            </div>
          )}
          <div>
            <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Created</div>
            <div className="mt-0.5">{fmtDT(task.created_at)}</div>
          </div>
        </div>

        {/* Contextual banners for pending states */}
        {task.status === "closure_requested" && (
          <div className="mt-4 rounded-lg border border-purple-500/30 bg-purple-500/5 p-3 text-sm" data-testid="task-closure-banner">
            <div className="font-semibold text-purple-700 mb-1">Closure requested</div>
            <div className="text-muted-foreground text-xs">{task.closure_note || "No note provided"}</div>
          </div>
        )}
        {task.status === "extension_requested" && (
          <div className="mt-4 rounded-lg border border-purple-500/30 bg-purple-500/5 p-3 text-sm" data-testid="task-extension-banner">
            <div className="font-semibold text-purple-700 mb-1 flex items-center gap-2">
              <CalendarClock className="w-4 h-4" /> Extension requested → new due <strong>{fmtDT(task.proposed_due_at)}</strong>
            </div>
            <div className="text-muted-foreground text-xs">{task.extension_reason}</div>
          </div>
        )}
        {task.status === "declined" && (
          <div className="mt-4 rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-sm">
            <div className="font-semibold text-rose-600 mb-1">Task declined</div>
            <div className="text-muted-foreground text-xs">{task.declined_reason}</div>
          </div>
        )}

        {/* Action bar */}
        <div className="mt-4 flex gap-2 flex-wrap">
          {/* Assignee actions */}
          {isAssignee && (task.status === "assigned" || task.status === "open") && (
            <>
              <Button size="sm" onClick={() => call(`/crm/tasks/${id}/accept`, {}, "Task accepted")} data-testid="task-accept">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Accept
              </Button>
              <Button size="sm" variant="outline" onClick={() => setDeclineOpen(true)} data-testid="task-decline">
                <XCircle className="w-4 h-4 mr-1.5" /> Decline
              </Button>
            </>
          )}
          {isAssignee && ["accepted", "in_progress", "reopened"].includes(task.status) && (
            <>
              <Button size="sm" onClick={() => setCloseOpen(true)} data-testid="task-request-closure">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Request closure
              </Button>
              <Button size="sm" variant="outline" onClick={() => setExtOpen(true)} data-testid="task-request-extension">
                <CalendarClock className="w-4 h-4 mr-1.5" /> Request extension
              </Button>
            </>
          )}

          {/* Admin/owner actions */}
          {canAdminAct && task.status === "closure_requested" && (
            <>
              <Button size="sm" onClick={() => { setDecOpen("closure_accept"); setDecNote(""); }} data-testid="task-decision-accept">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Accept closure
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setDecOpen("closure_reject"); setDecNote(""); }} data-testid="task-decision-reject">
                <XCircle className="w-4 h-4 mr-1.5" /> Reject & reopen
              </Button>
            </>
          )}
          {canAdminAct && task.status === "extension_requested" && (
            <>
              <Button size="sm" onClick={() => { setDecOpen("ext_accept"); setDecNote(""); setDecNewDue(task.proposed_due_at?.slice(0,16) || ""); }} data-testid="task-ext-accept">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve extension
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setDecOpen("ext_reject"); setDecNote(""); }} data-testid="task-ext-reject">
                <XCircle className="w-4 h-4 mr-1.5" /> Reject
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Documents pane */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <DocList title="Admin documents" docs={adminDocs} testid="task-admin-docs" />
        <DocList title="Employee documents" docs={empDocs} testid="task-emp-docs" />
      </div>

      {/* Chat / progress thread */}
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-4 h-4 text-muted-foreground" />
          <h2 className="font-semibold text-sm">Progress & comments</h2>
        </div>
        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2" data-testid="task-thread">
          {thread.length === 0 && (
            <div className="text-center text-xs text-muted-foreground py-6">No updates yet.</div>
          )}
          {thread.map(n => {
            const mine = n.author_id === user?.id;
            return (
              <div key={n.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                  {!mine && <div className="text-[10px] opacity-70 mb-0.5 font-semibold">{n.author_name}</div>}
                  <div className="whitespace-pre-wrap break-words">{n.body}</div>
                  {n.files?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {n.files.map(f => <FileChip key={f.id} f={f} onLight={mine} />)}
                    </div>
                  )}
                  <div className={`text-[10px] mt-1 ${mine ? "opacity-70" : "text-muted-foreground"}`}>{fmtDT(n.created_at)}</div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {canChat && (
          <div className="mt-4 space-y-2 border-t pt-3">
            <Textarea
              placeholder="Add a progress update or comment…"
              value={msg}
              onChange={e => setMsg(e.target.value)}
              rows={2}
              data-testid="task-msg-input"
            />
            <AttachmentComposer files={attachments} onChange={setAttachments} compact />
            <div className="flex justify-end">
              <Button size="sm" onClick={send} data-testid="task-msg-send">
                <Send className="w-3.5 h-3.5 mr-1.5" /> Send
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* --- Dialogs --- */}
      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Decline task</DialogTitle></DialogHeader>
          <Label className="text-xs">Reason *</Label>
          <Textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)} rows={3} data-testid="task-decline-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={async () => {
              if (!declineReason.trim()) return toast.error("Reason required");
              const ok = await call(`/crm/tasks/${id}/decline`, { reason: declineReason }, "Task declined");
              if (ok) { setDeclineOpen(false); setDeclineReason(""); }
            }} data-testid="task-decline-submit">Decline</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request closure</DialogTitle>
            <DialogDescription className="text-xs">Explain what's done. Attach evidence if you have any.</DialogDescription>
          </DialogHeader>
          <Label className="text-xs">Closure note</Label>
          <Textarea value={closeNote} onChange={e => setCloseNote(e.target.value)} rows={3} data-testid="task-close-note" />
          <Label className="text-xs">Attachments</Label>
          <AttachmentComposer files={closeFiles} onChange={setCloseFiles} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)}>Cancel</Button>
            <Button onClick={async () => {
              const ok = await call(`/crm/tasks/${id}/request-closure`, {
                note: closeNote, file_ids: closeFiles.map(f => f.id),
              }, "Closure requested");
              if (ok) { setCloseOpen(false); setCloseNote(""); setCloseFiles([]); }
            }} data-testid="task-close-submit">Request closure</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={extOpen} onOpenChange={setExtOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request extension</DialogTitle>
            <DialogDescription className="text-xs">Propose a new due date + explain why.</DialogDescription>
          </DialogHeader>
          <Label className="text-xs">Proposed new due date *</Label>
          <Input type="datetime-local" value={extDate} onChange={e => setExtDate(e.target.value)} data-testid="task-ext-date" />
          <Label className="text-xs mt-2">Reason *</Label>
          <Textarea value={extReason} onChange={e => setExtReason(e.target.value)} rows={3} data-testid="task-ext-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtOpen(false)}>Cancel</Button>
            <Button onClick={async () => {
              if (!extDate || !extReason.trim()) return toast.error("Both fields required");
              const ok = await call(`/crm/tasks/${id}/request-extension`, {
                proposed_due_at: new Date(extDate).toISOString(),
                reason: extReason,
              }, "Extension requested");
              if (ok) { setExtOpen(false); setExtDate(""); setExtReason(""); }
            }} data-testid="task-ext-submit">Request extension</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin decision dialogs */}
      <Dialog open={!!decOpen} onOpenChange={(o) => !o && setDecOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {decOpen === "closure_accept" && "Accept closure"}
              {decOpen === "closure_reject" && "Reject closure & reopen"}
              {decOpen === "ext_accept" && "Approve extension"}
              {decOpen === "ext_reject" && "Reject extension"}
            </DialogTitle>
          </DialogHeader>
          {decOpen === "ext_accept" && (
            <>
              <Label className="text-xs">New due date (defaults to proposed)</Label>
              <Input type="datetime-local" value={decNewDue} onChange={e => setDecNewDue(e.target.value)} data-testid="task-dec-new-due" />
            </>
          )}
          <Label className="text-xs mt-2">Note (optional)</Label>
          <Textarea value={decNote} onChange={e => setDecNote(e.target.value)} rows={2} data-testid="task-dec-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecOpen(null)}>Cancel</Button>
            <Button onClick={async () => {
              const decisionMap = {
                closure_accept: "accept",
                closure_reject: "reject",
                ext_accept: "approve_extension",
                ext_reject: "reject_extension",
              };
              const body = { decision: decisionMap[decOpen], note: decNote };
              if (decOpen === "ext_accept" && decNewDue) body.new_due_at = new Date(decNewDue).toISOString();
              const ok = await call(`/crm/tasks/${id}/decision`, body, "Decision recorded");
              if (ok) { setDecOpen(null); setDecNote(""); setDecNewDue(""); }
            }} data-testid="task-dec-submit">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DocList({ title, docs, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-muted-foreground" />
        <h3 className="font-semibold text-sm">{title}</h3>
        <span className="text-xs text-muted-foreground">({docs.length})</span>
      </div>
      {docs.length === 0 ? (
        <div className="text-xs text-muted-foreground py-3">None yet.</div>
      ) : (
        <ul className="space-y-1.5">
          {docs.map(f => <li key={f.id}><FileChip f={f} /></li>)}
        </ul>
      )}
    </div>
  );
}

function FileChip({ f, onLight }) {
  const isImg = (f.content_type || "").startsWith("image/");
  const url = `/api/files/${f.id}/download`;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 text-xs rounded-md px-2 py-1 border ${onLight ? "border-white/40 bg-white/10" : "border-border bg-muted hover:bg-muted/70"}`}
    >
      {isImg ? <Paperclip className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
      <span className="truncate max-w-[160px]">{f.original_filename || f.id.slice(0,8)}</span>
      <Download className="w-3 h-3 opacity-60" />
    </a>
  );
}
